// The World's board registry as the Desk reads it back (RM2/RM5): a board row
// the authority has not populated is completed to the BD6 read shape (no tools
// seated, nothing selected, the default governing scopes) before it is decoded
// as a BoardEntitledRow. One function for both hosts of apps/web: the Desk's
// Mac server applies it to the World door's answer (worldDoorHttp.ts), and
// cleo-web applies it to the World's own answer in the browser
// (neucleos/webHostWorldDoors.ts), so a registry reads the same on either.

// A board row without an authored tool list seats no tools and selects
// nothing — the general-room profile, not a law about every session. This
// fallback only completes the BD6 read shape for a row the authority has not
// populated; it never widens.
const LEGACY_GENERAL_ROOM_TOOLS: ReadonlyArray<string> = [];

const completeLegacyBoard = (entry: unknown, entitled: boolean): unknown => {
  if (typeof entry !== "object" || entry === null || Array.isArray(entry)) return entry;
  const board = entry as Record<string, unknown>;
  return {
    ...board,
    tools: board["tools"] ?? LEGACY_GENERAL_ROOM_TOOLS,
    defaultSelection: board["defaultSelection"] ?? { mode: "none" },
    ...(entitled
      ? {
          toolsGoverningScope: board["toolsGoverningScope"] ?? "default",
          defaultSelectionGoverningScope: board["defaultSelectionGoverningScope"] ?? "default",
        }
      : {}),
  };
};

export const completeLegacyBoardProfiles = (value: unknown): unknown => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return value;
  const registry = value as Record<string, unknown>;
  if (!Array.isArray(registry["boards"])) return value;
  return {
    ...registry,
    boards: registry["boards"].map((entry) => completeLegacyBoard(entry, true)),
  };
};

export const completeLegacyBoardMatrix = (value: unknown): unknown => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return value;
  const matrix = value as Record<string, unknown>;
  if (!Array.isArray(matrix["boards"]) || !Array.isArray(matrix["worlds"])) return value;
  const completedBoards = matrix["boards"].map((entry) => completeLegacyBoard(entry, false));
  const defaultProfiles = Object.fromEntries(
    completedBoards.flatMap((entry) => {
      if (typeof entry !== "object" || entry === null || Array.isArray(entry)) return [];
      const board = entry as Record<string, unknown>;
      return typeof board["key"] === "string"
        ? [
            [
              board["key"],
              {
                tools: board["tools"],
                toolsGoverningScope: "default",
                defaultSelection: board["defaultSelection"],
                defaultSelectionGoverningScope: "default",
              },
            ],
          ]
        : [];
    }),
  );
  return {
    ...matrix,
    boards: completedBoards,
    worlds: matrix["worlds"].map((worldEntry) => {
      if (typeof worldEntry !== "object" || worldEntry === null || Array.isArray(worldEntry))
        return worldEntry;
      const world = worldEntry as Record<string, unknown>;
      if (!Array.isArray(world["members"])) return worldEntry;
      return {
        ...world,
        profiles: world["profiles"] ?? defaultProfiles,
        members: world["members"].map((memberEntry) => {
          if (typeof memberEntry !== "object" || memberEntry === null || Array.isArray(memberEntry))
            return memberEntry;
          const member = memberEntry as Record<string, unknown>;
          return {
            ...member,
            boards: Array.isArray(member["boards"])
              ? member["boards"].map((entry) => completeLegacyBoard(entry, true))
              : member["boards"],
          };
        }),
      };
    }),
  };
};
